// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState } from 'react';
import { connect } from 'react-redux';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MenuInfo } from 'rc-menu/lib/interface';
import ActionsMenuComponent, { Actions } from 'components/actions-menu/actions-menu';
import CVATLoadingSpinner from 'components/common/loading-spinner';
import { CombinedState } from 'reducers';

import { modelsActions } from 'actions/models-actions';
import {
    deleteTaskAsync,
    switchMoveTaskModalVisible,
} from 'actions/tasks-actions';
import { exportActions } from 'actions/export-actions';
import { importActions } from 'actions/import-actions';
import User from 'cvat-core/src/user';
import { getCore } from 'cvat-core-wrapper';
import { SerializedTaskReport, SerializedJobReport } from 'cvat-core/src/project-report';
import { GetJobStageChinese, GetJobStateChinese } from 'cvat-core/src/enums';

import Modal from 'antd/lib/modal';

const core = getCore();

interface OwnProps {
    taskInstance: any;
    onViewAnalytics: () => void;
}

interface StateToProps {
    user: User;
    annotationFormats: any;
    inferenceIsActive: boolean;
    backupIsActive: boolean;
}

interface DispatchToProps {
    showExportModal: (taskInstance: any, resource: 'dataset' | 'backup') => void;
    showImportModal: (taskInstance: any) => void;
    openRunModelWindow: (taskInstance: any) => void;
    deleteTask: (taskInstance: any) => void;
    openMoveTaskToProjectWindow: (taskInstance: any) => void;
}

function mapStateToProps(state: CombinedState, own: OwnProps): StateToProps {
    const {
        taskInstance: { id: tid },
    } = own;

    const {
        auth: { user },
        formats: { annotationFormats },
    } = state;

    return {
        user,
        annotationFormats,
        inferenceIsActive: tid in state.models.taskInferences,
        backupIsActive: state.export.tasks.backup.current[tid],
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        showExportModal: (taskInstance: any, resource: 'dataset' | 'backup'): void => {
            if (resource === 'dataset') {
                dispatch(exportActions.openExportDatasetModal(taskInstance));
            } else {
                dispatch(exportActions.openExportBackupModal(taskInstance));
            }
        },
        showImportModal: (taskInstance: any): void => {
            dispatch(importActions.openImportDatasetModal(taskInstance));
        },
        deleteTask: (taskInstance: any): void => {
            dispatch(deleteTaskAsync(taskInstance));
        },
        openRunModelWindow: (taskInstance: any): void => {
            dispatch(modelsActions.showRunModelDialog(taskInstance));
        },
        openMoveTaskToProjectWindow: (taskId: number): void => {
            dispatch(switchMoveTaskModalVisible(true, taskId));
        },
    };
}

function downloadTaskReportAsCSV(id: number, report: SerializedTaskReport): void {
    // 生成 CSV 文件内容
    let csvContent = '题包,标注人员,标注框数,当前阶段,当前状态\n';

    report.jobs.forEach((job: SerializedJobReport) => {
        csvContent += `${job.job_id},${job.assignee},${job.labeledshapes},${
            GetJobStageChinese(job.stage)}, ${GetJobStateChinese(job.state)}\n`;
    });

    // 创建一个 Blob 对象
    const blob = new Blob([csvContent], { type: 'text/csv' });

    // 创建一个下载链接
    const url = URL.createObjectURL(blob);

    // 创建一个 a 元素并触发下载
    const a = document.createElement('a');
    a.href = url;
    a.download = `${id}.csv`;
    document.body.appendChild(a);
    a.click();

    // 释放 URL 对象
    URL.revokeObjectURL(url);

    // 移除 a 元素
    document.body.removeChild(a);
}

function ActionsMenuContainer(props: OwnProps & StateToProps & DispatchToProps): JSX.Element {
    const {
        user,
        taskInstance,
        annotationFormats: { loaders, dumpers },
        inferenceIsActive,
        backupIsActive,
        showExportModal,
        showImportModal,
        deleteTask,
        openRunModelWindow,
        openMoveTaskToProjectWindow,
        onViewAnalytics,
    } = props;

    const [updatingTask, setUpdatingTask] = useState(false);

    const onClickMenu = (params: MenuInfo): void | JSX.Element => {
        const [action] = params.keyPath;
        if (action === Actions.EXPORT_TASK_DATASET) {
            showExportModal(taskInstance, 'dataset');
        } else if (action === Actions.LOCK_TASK) {
            setUpdatingTask(true);
            taskInstance.locked = true;
            taskInstance.save().then(() => {
                setUpdatingTask(false);
                Modal.info({
                    icon: null,
                    className: 'cvat-modal-content-finish-job',
                    content: (
                        <>
                            <div>
                                任务 #
                                {taskInstance.id}
                                {' '}
                                锁定成功！
                            </div>
                        </>
                    ),
                });
                window.location.reload();
            });
        } else if (action === Actions.UNLOCK_TASK) {
            setUpdatingTask(true);
            taskInstance.locked = false;
            taskInstance.save().then(() => {
                setUpdatingTask(false);
                Modal.info({
                    icon: null,
                    className: 'cvat-modal-content-finish-job',
                    content: (
                        <>
                            <div>
                                任务 #
                                {taskInstance.id}
                                {' '}
                                解锁成功！
                            </div>
                        </>
                    ),
                });
                window.location.reload();
            });
        } else if (action === Actions.DELETE_TASK) {
            deleteTask(taskInstance);
        } else if (action === Actions.OPEN_BUG_TRACKER) {
            window.open(`${taskInstance.bugTracker}`, '_blank');
        } else if (action === Actions.RUN_AUTO_ANNOTATION) {
            openRunModelWindow(taskInstance);
        } else if (action === Actions.BACKUP_TASK) {
            showExportModal(taskInstance, 'backup');
        } else if (action === Actions.MOVE_TASK_TO_PROJECT) {
            openMoveTaskToProjectWindow(taskInstance.id);
        } else if (action === Actions.LOAD_TASK_ANNO) {
            showImportModal(taskInstance);
        } else if (action === Actions.VIEW_ANALYTICS) {
            onViewAnalytics();
        } else if (action === Actions.EXPORT_TASK_REPORTS) {
            Promise.all([core.tasks.report(taskInstance.id)]).then((results) => {
                const report = results[0];
                downloadTaskReportAsCSV(taskInstance.id, report);
            });
        }
    };

    return (
        <>
            { updatingTask ? <CVATLoadingSpinner size='large' /> : null }
            <ActionsMenuComponent
                user={user}
                taskID={taskInstance.id}
                taskLocked={taskInstance.locked}
                projectID={taskInstance.projectId}
                taskMode={taskInstance.mode}
                bugTracker={taskInstance.bugTracker}
                loaders={loaders}
                dumpers={dumpers}
                inferenceIsActive={inferenceIsActive}
                onClickMenu={onClickMenu}
                taskDimension={taskInstance.dimension}
                backupIsActive={backupIsActive}
            />
        </>

    );
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionsMenuContainer);
